import { createElement, forwardRef, useEffect, useState } from 'react';
import { ListDesign, ListItem, ListSeparator, VirtualListDesign } from '../ListBox';
import { Popover, PopoverRef } from '../Popover';
import { postfixTestId } from '../../util/testId';
import {
  OptionPopperOption,
  OptionPopperProps,
  OptionPopperSeparator,
  VirtualOptionPopperItem,
} from './types';
import { setRef } from '../../util/ref-util';

import cn from './OptionPopper.css.json';
import './OptionPopper.css';

/**
 * A popper specifically for Inputs, Dropdowns, and Breadcrumbs. Please don't try to reuse
 * this for anything else unless it follows the same behavior (and is supposed to).
 */
export const OptionPopper = forwardRef<HTMLDivElement, OptionPopperProps>(function OptionPopper(
  props,
  ref
) {
  const [minWidth, setMinWidth] = useState<string | number | undefined>(undefined);
  const elementWidth = props.referenceElementRef.current?.getBoundingClientRect().width;

  useEffect(() => {
    if (props.referenceElementRef.current) {
      setMinWidth(elementWidth);
    }
  }, [props.referenceElementRef, elementWidth]);

  let separatorIndex = -1;
  let itemIndex = -1;
  const getOption = (item: OptionPopperOption | VirtualOptionPopperItem | null) => {
    if (item && !props.displayListItemIcon) {
      // remove icon if not displaying icons
      const { icon, ...listItem } = item;
      return listItem;
    }
    return item;
  };
  const getListItem = (
    item: OptionPopperOption | VirtualOptionPopperItem | null,
    index: number
  ) => (
    <ListItem
      {...getOption(item)}
      onActionPress={props.onActionPress}
      onSelected={props.onSelected}
      index={index}
      key={index}
      selected={props.focusedIndex === index}
      showSelectedPseudoFocus={props.showSelectedPseudoFocus}
      focused={!!props.listItemFocusable && props.focusedIndex === index}
      focusable={!!props.listItemFocusable && props.focusedIndex === index}
      ref={props.focusedIndex === index ? props.focusedItemRef : undefined}
      testId={postfixTestId(props.testId, `list-listItem-${index}`)}
      tokens={props.listItemTokens}
      disabled={props.isItemDisabled?.(index)}
    />
  );

  const listProps = {
    selectedIndex: props.selectedIndex,
    setFocusedIndex: props.setFocusedIndex,
    onSelectFocusedItem: props.onSelectFocusedItem,
    testId: postfixTestId(props.testId, 'list'),
    ...props.listTokens,
    id: props.listId,
    ariaLabelledBy: props.ariaLabelledBy,
    ariaLabel: props.ariaLabel,
    ref: props.ulRef,
  };

  return (
    <Popover
      referenceElementRef={props.referenceElementRef}
      isOpen={props.isOpen}
      placement="bottom-start"
      hideArrow
      style={{
        ...props.style,
        maxHeight: props.style?.maxHeight ?? '75vh',
        minWidth: props.style?.minWidth ?? minWidth,
      }}
      className={cn.optionPopper}
      testId={postfixTestId(props.testId, 'optionPopper')}
      ref={(popoverRef: PopoverRef) => {
        if (popoverRef?.container) {
          setRef(ref, popoverRef?.container);
        }
      }}
      onClose={props.onClose}
      returnFocusOnEscape={props.returnFocusOnEscape}
      focusLockProps={props.focusLockProps}
      {...props.popoverTokens}
      disableContextMenu={props.disableContextMenu}
    >
      {props.virtualItems ? (
        <VirtualListDesign
          count={props.virtualItems.count}
          renderItem={({ index }) =>
            getListItem(props.virtualItems?.renderItem({ index }) ?? null, index)
          }
          virtualizerRef={props.virtualItems.virtualizerRef}
          pageSizeRef={props.virtualItems.pageSizeRef}
          itemHeight={props.virtualItems.itemHeight}
          style={{
            width: `${props.virtualItems?.width ?? 150}px`,
            height: `100%`,
            // needs this explicitly set so list's scrolling container
            // has the same max-height
            maxHeight: props.style?.maxHeight ?? '75vh',
          }}
          {...listProps}
        />
      ) : (
        <ListDesign className={cn.optionPopperList} {...listProps}>
          {!props.items
            ? null
            : props.items.map((item) => {
                if ((item as OptionPopperSeparator).separator) {
                  separatorIndex++;
                  return <ListSeparator key={`${separatorIndex}-separator`} />;
                } else {
                  itemIndex++;
                  return getListItem(item, itemIndex);
                }
              })}
        </ListDesign>
      )}
    </Popover>
  );
});
